import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Clock, ChefHat, Wine, Flame, ArrowRight, RotateCcw, Undo2 } from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import ReceiptPrint from '../components/ReceiptPrint';
import QuickLinksModal from '../components/QuickLinksModal';
import RecipePopover from '../components/RecipePopover';
import ServiceRequestListener from '../components/ServiceRequestListener';
import AlertPrefsButton from '../components/AlertPrefsButton';
import StaffHeaderMenu from '../components/StaffHeaderMenu';
import PanelViewToggle from '../components/PanelViewToggle';
import AllLiveOrdersView from '../components/AllLiveOrdersView';
import { useOrderAlert } from '../lib/orderAlert';
import { tableLabel } from '../lib/tableLabel';

const panelConfig = {
  kitchen: {
    category: 'food',
    icon: ChefHat,
    color: 'gold',
    label: 'Kitchen Panel',
    subtitle: 'Food waiting to be cooked',
    iconBgCls: 'bg-gold/10',
    iconCls: 'text-gold',
    breadcrumbCls: 'text-gold',
    pillCls: 'bg-gold/15 text-gold border-gold/50',
  },
  bar: {
    category: 'drinks',
    icon: Wine,
    color: 'blue-accent',
    label: 'Bar Panel',
    subtitle: 'Drinks waiting to be made',
    iconBgCls: 'bg-blue-accent/10',
    iconCls: 'text-blue-accent',
    breadcrumbCls: 'text-blue-accent',
    pillCls: 'bg-blue-accent/15 text-blue-accent border-blue-accent/50',
  },
  shisha: {
    category: 'shisha',
    icon: Flame,
    color: 'gold',
    label: 'Shisha Panel',
    subtitle: 'Shisha waiting to be prepared',
    iconBgCls: 'bg-amber-500/10',
    iconCls: 'text-amber-400',
    breadcrumbCls: 'text-amber-400',
    pillCls: 'bg-amber-500/15 text-amber-300 border-amber-500/50',
  },
};

const delayThresholds = { food: 10, drinks: 5, shisha: 10 };

function getElapsedMinutes(timestamp) {
  return Math.floor((Date.now() - new Date(timestamp).getTime()) / 60000);
}

function TimerDisplay({ timestamp, category }) {
  const [elapsed, setElapsed] = useState(getElapsedMinutes(timestamp));
  useEffect(() => { const i = setInterval(() => setElapsed(getElapsedMinutes(timestamp)), 10000); return () => clearInterval(i); }, [timestamp]);

  const threshold = delayThresholds[category] || 10;
  const colorClass = elapsed >= threshold ? 'timer-danger' : elapsed >= threshold * 0.7 ? 'timer-warning' : 'timer-ok';

  return (
    <span className={`font-mono text-sm ${colorClass} flex items-center gap-1`}>
      <Clock className="w-3 h-3" /> {elapsed}m
    </span>
  );
}

export default function PrepPanel({ type, title, embedded = false }) {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [recentlyServed, setRecentlyServed] = useState([]);
  const [showRecentlyServed, setShowRecentlyServed] = useState(false);
  const [updating, setUpdating] = useState('');
  const [quickLinksOpen, setQuickLinksOpen] = useState(false);
  // 'mine' = own work panel (default). 'all' = cross-panel read-only view.
  // Persisted per panel-type so each device remembers per role.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = localStorage.getItem(`majnu:panelView:${type}`);
      return v === 'all' ? 'all' : 'mine';
    } catch { return 'mine'; }
  });
  const onChangeViewMode = (v) => {
    setViewMode(v);
    try { localStorage.setItem(`majnu:panelView:${type}`, v); } catch { /* sandboxed */ }
  };
  const config = panelConfig[type];
  const canPrint = user?.role === 'bartender' || user?.role === 'manager' || user?.role === 'director';
  const showRecipes = type === 'bar' && ['bartender', 'manager', 'director'].includes(user?.role);

  const [fetchError, setFetchError] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/panel/${type}`);
      setOrders(data);
      setFetchError('');
    } catch (err) {
      // Surface errors instead of silently swallowing — staff need to know
      // if their PWA lost its session or the network is down.
      if (err?.response?.status === 401) {
        setFetchError('Session expired — please log in again');
      } else if (!navigator.onLine) {
        setFetchError('No internet connection');
      } else {
        setFetchError('Connection issue — retrying…');
      }
    }
    // "Recently Served" feeds the send-back drawer. Only kitchen/bar/shisha
    // panels support it (serving panel doesn't manage prep state).
    if (type !== 'serving') {
      try {
        const { data: served } = await api.get(`/orders/panel/${type}/recently-served`);
        setRecentlyServed(served || []);
      } catch {
        // non-fatal — keep prior list to avoid flicker
      }
    }
  }, [type]);

  // Tight polling for new orders + immediate refetch when the PWA comes back
  // to foreground. iOS/Android suspend JS intervals when backgrounded, so we
  // need an explicit "wake up" handler. Otherwise staff lock their phone, come
  // back, and the panel shows stale data with no indication.
  useEffect(() => {
    fetchOrders();
    const i = setInterval(fetchOrders, 5000);
    const onVisibility = () => { if (document.visibilityState === 'visible') fetchOrders(); };
    const onFocus = () => fetchOrders();
    const onOnline = () => fetchOrders();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    return () => {
      clearInterval(i);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, [fetchOrders]);

  const updateStatus = async (orderId, itemId, newStatus) => {
    setUpdating(`${orderId}-${itemId}`);
    try {
      await api.put(`/orders/${orderId}/items/${itemId}/status`, { status: newStatus });
      toast.success(`Marked as ${newStatus}`);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setUpdating('');
    }
  };

  // Bulk-advance every item on an order from one status to the next. Lets the
  // chef tap the whole order card on a tablet instead of hunting for tiny
  // per-item buttons. Runs sequentially to avoid a race: the backend reads
  // the order doc, mutates items array, and writes it back — two parallel
  // PUTs would clobber each other and only one item would actually progress.
  const advanceOrder = async (order, fromStatus, toStatus) => {
    const items = order.items.filter(i => i.status === fromStatus);
    if (items.length === 0) return;
    setUpdating(`order-${order.id}`);
    let progressed = 0;
    try {
      for (const it of items) {
        try {
          await api.put(`/orders/${order.id}/items/${it.id}/status`, { status: toStatus });
          progressed++;
        } catch (err) {
          toast.error(err.response?.data?.detail || `Failed on ${it.name}`);
          break;
        }
      }
      if (progressed > 0) {
        toast.success(progressed === 1
          ? `Marked ${items[0].name} as ${toStatus}`
          : `Marked ${progressed} items as ${toStatus}`);
      }
      fetchOrders();
    } finally {
      setUpdating('');
    }
  };

  const receivedOrders = orders.filter(o => o.items.some(i => i.status === 'received'));
  const preparingOrders = orders.filter(o => o.items.some(i => i.status === 'preparing'));

  // Looping audio + vibration alert: rings while at least one order on THIS
  // panel is still completely UNTOUCHED (every item in 'received'). The
  // moment chef/bartender/shisha advances any item to preparing/ready, that
  // order drops out of pendingIds and the loop stops. Final stop happens
  // automatically when no orders are pending.
  const { prefs, updatePrefs, playSample } = useOrderAlert({
    role: user?.role,
    orders,
    getReceivedItems: (o) => {
      const items = o.items || [];
      // Only consider the order "pending acknowledgment" if every item is still in received.
      const allUntouched = items.length > 0 && items.every(i => i.status === 'received');
      return allUntouched ? items : [];
    },
  });

  const Icon = config.icon;

  return (
    <div className={embedded ? '' : 'min-h-screen bg-bg-primary'}>
      {!embedded && <ServiceRequestListener />}
      {/* Focused header — owner request Feb 2026: kitchen + shisha panels
          should look like the bartender's Bar Panel (icon + Title › Prep
          Queue breadcrumb + single PREP QUEUE pill below, nothing else
          competing for attention). Suppressed when `embedded` so the
          BarPanel tabbed shell keeps owning its own header for till
          supervisors. */}
      {!embedded && (
      <header className="sticky top-0 z-30 glass-header px-3 py-2.5 sm:px-4 sm:py-3 flex items-center justify-between gap-3 flex-wrap border-b border-white/10">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={`w-10 h-10 rounded-sm ${config.iconBgCls} flex items-center justify-center shrink-0`}>
            <Icon className={`w-5 h-5 ${config.iconCls}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-base sm:text-lg font-serif text-white truncate leading-none" data-testid="panel-title">{config.label}</h1>
              <span className="text-zinc-600">›</span>
              <span className={`text-base sm:text-lg font-serif leading-none ${config.breadcrumbCls}`} data-testid="panel-current-section">
                Prep Queue
              </span>
            </div>
            <p className="text-[10px] sm:text-xs text-zinc-500 font-sans mt-1 truncate" data-testid="panel-subtitle">
              {config.subtitle} · {user?.name}
            </p>
          </div>
          {fetchError && (
            <Badge
              className="bg-red-500/15 text-red-300 border border-red-500/30 font-sans text-[10px] uppercase tracking-[0.18em] animate-pulse"
              data-testid="panel-fetch-error"
              title={fetchError}
            >
              {fetchError.includes('Session') ? 'Logged out' : 'Offline'}
            </Badge>
          )}
        </div>

        {/* Controls — trimmed to match the focused bar-panel look. The
            cross-panel Jump-to buttons live on the manager dashboards
            instead; this view is purely "what do I need to make next?". */}
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end shrink-0">
          {/* View toggle stays for manager/director — they sometimes need
              the cross-panel "all orders" read-only view. Chef + shisha
              expert get the single focused queue (no toggle clutter). */}
          {['manager', 'director'].includes(user?.role) && (
            <PanelViewToggle value={viewMode} onChange={onChangeViewMode} />
          )}
          <AlertPrefsButton prefs={prefs} onChange={updatePrefs} onTest={playSample} />
          <NotificationBell />
          <StaffHeaderMenu
            onOpenQuickLinks={() => setQuickLinksOpen(true)}
            onLogout={logout}
          />
        </div>
      </header>
      )}

      {/* Big PREP QUEUE pill — single tab strip mirroring the Bar Panel
          shell so the three panels (bar / kitchen / shisha) all share
          the exact same visual language. The pill is decorative
          (there's only one tab) but anchors the eye and matches the
          bartender's view they're used to. */}
      {!embedded && (
        <div
          className="sticky top-[68px] z-20 w-full border-b border-white/10 bg-bg-primary/95 backdrop-blur-md px-2 py-2"
          data-testid="prep-queue-tabstrip"
        >
          <div
            className={`inline-flex items-center gap-2 text-[12px] sm:text-sm uppercase tracking-[0.12em] h-11 px-3.5 sm:px-4 rounded-sm border ${config.pillCls}`}
            data-testid="prep-queue-tab"
          >
            <Icon className="w-4 h-4" />
            <span className="font-semibold">Prep Queue</span>
            <Badge className="ml-0.5 font-mono text-[10px] px-1.5 bg-white/5 text-zinc-300 border border-white/10" data-testid="prep-queue-count">
              {orders.length} active
            </Badge>
          </div>
        </div>
      )}

      {!embedded && <QuickLinksModal open={quickLinksOpen} onClose={() => setQuickLinksOpen(false)} />}

      {/* Content */}
      {viewMode === 'all' ? (
        <AllLiveOrdersView myPanelKey={type} />
      ) : (
        <div className="p-4">
        {orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Icon className="w-12 h-12 text-zinc-700 mb-4" />
            <p className="text-zinc-500 font-sans" data-testid="no-orders-message">No orders to prepare</p>
          </div>
        ) : (
          <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Received Column */}
            <div>
              <h2 className="text-sm uppercase tracking-[0.2em] text-zinc-400 font-sans font-bold mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-accent" /> Received ({receivedOrders.length})
              </h2>
              <div className="space-y-3">
                {receivedOrders.map(order => {
                  const recvItems = order.items.filter(i => i.status === 'received');
                  const cardBusy = updating === `order-${order.id}`;
                  return (
                  <div key={order.id} className={`bg-white/[0.03] border border-white/5 rounded-sm overflow-hidden ${cardBusy ? 'opacity-60' : ''}`} data-testid={`prep-order-${order.id}`}>
                    <div className="p-4">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        {order.is_takeaway ? (
                          <>
                            <span className="text-[10px] uppercase tracking-[0.18em] bg-orange-500/15 text-orange-400 border border-orange-500/40 px-2 py-0.5 rounded-sm font-sans font-bold" data-testid={`takeaway-badge-${order.id}`}>Takeaway</span>
                            {order.pickup_label && <span className="text-[11px] font-sans text-orange-300">· {order.pickup_label}</span>}
                          </>
                        ) : (
                          <span className="text-gold font-serif text-2xl font-semibold">{tableLabel(order.table_number)}</span>
                        )}
                        <span className="text-sm text-zinc-500 font-sans">#{order.id.slice(0, 6)}</span>
                        {order.customer_name && <span className="text-sm text-zinc-300 font-sans truncate">· {order.customer_name}</span>}
                        {order.is_takeaway && order.customer_phone && <span className="text-[10px] text-zinc-500 font-mono truncate" data-testid={`takeaway-phone-${order.id}`}>{order.customer_phone}</span>}
                        {order.is_guest && !order.is_takeaway && <span className="text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded-sm font-sans" data-testid={`guest-badge-${order.id}`}>Guest</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        {canPrint && <ReceiptPrint orderId={order.id} />}
                        <TimerDisplay timestamp={order.created_at} category={config.category} />
                      </div>
                    </div>
                    {recvItems.map(item => (
                      <div key={item.id} className="py-2 border-t border-white/5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0 flex-wrap">
                            <span className="text-white font-sans text-base truncate">{item.quantity}x {item.name}</span>
                            {showRecipes && item.item_id && (
                              <RecipePopover itemId={item.item_id} itemName={item.name} />
                            )}
                          </div>
                          <Button size="sm" disabled={updating === `${order.id}-${item.id}` || cardBusy} onClick={(e) => { e.stopPropagation(); updateStatus(order.id, item.id, 'preparing'); }} className="h-9 bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 rounded-sm font-sans text-sm px-3" data-testid={`start-prep-${item.id}`}>
                            Start <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                        {item.instructions && (
                          <p className="mt-1 text-amber-300 text-sm font-sans bg-amber-500/10 border border-amber-500/30 rounded-sm px-2 py-1.5" data-testid={`item-note-${item.id}`}>
                            📝 {item.instructions}
                          </p>
                        )}
                      </div>
                    ))}
                    </div>
                    {/* Big tap target — entire bottom strip moves all items to Preparing */}
                    <button
                      onClick={() => advanceOrder(order, 'received', 'preparing')}
                      disabled={cardBusy}
                      className="w-full bg-amber-500/15 hover:bg-amber-500/25 active:bg-amber-500/30 text-amber-300 font-sans text-base font-semibold py-4 flex items-center justify-center gap-2 border-t border-amber-500/20 transition-colors disabled:opacity-50"
                      data-testid={`start-all-${order.id}`}
                    >
                      <ArrowRight className="w-4 h-4" /> Start preparing {recvItems.length > 1 ? `all ${recvItems.length} items` : ''}
                    </button>
                  </div>
                  );
                })}
              </div>
            </div>

            {/* Preparing Column */}
            <div>
              <h2 className="text-sm uppercase tracking-[0.2em] text-zinc-400 font-sans font-bold mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-400" /> Preparing ({preparingOrders.length})
              </h2>
              <div className="space-y-3">
                {preparingOrders.map(order => {
                  const prepItems = order.items.filter(i => i.status === 'preparing');
                  const cardBusy = updating === `order-${order.id}`;
                  return (
                  <div key={`prep-${order.id}`} className={`bg-white/[0.03] border border-amber-500/10 rounded-sm overflow-hidden ${cardBusy ? 'opacity-60' : ''}`} data-testid={`preparing-order-${order.id}`}>
                    <div className="p-4">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        {order.is_takeaway ? (
                          <>
                            <span className="text-[10px] uppercase tracking-[0.18em] bg-orange-500/15 text-orange-400 border border-orange-500/40 px-2 py-0.5 rounded-sm font-sans font-bold">Takeaway</span>
                            {order.pickup_label && <span className="text-[11px] font-sans text-orange-300">· {order.pickup_label}</span>}
                          </>
                        ) : (
                          <span className="text-gold font-serif text-2xl font-semibold">{tableLabel(order.table_number)}</span>
                        )}
                        <span className="text-sm text-zinc-500 font-sans">#{order.id.slice(0, 6)}</span>
                        {order.customer_name && <span className="text-sm text-zinc-300 font-sans truncate">· {order.customer_name}</span>}
                        {order.is_takeaway && order.customer_phone && <span className="text-[10px] text-zinc-500 font-mono truncate">{order.customer_phone}</span>}
                        {order.is_guest && !order.is_takeaway && <span className="text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded-sm font-sans">Guest</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        {canPrint && <ReceiptPrint orderId={order.id} />}
                        <TimerDisplay timestamp={order.created_at} category={config.category} />
                      </div>
                    </div>
                    {prepItems.map(item => (
                      <div key={item.id} className="py-2 border-t border-white/5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0 flex-wrap">
                            <span className="text-white font-sans text-base truncate">{item.quantity}x {item.name}</span>
                            {showRecipes && item.item_id && (
                              <RecipePopover itemId={item.item_id} itemName={item.name} />
                            )}
                          </div>
                          <Button size="sm" disabled={updating === `${order.id}-${item.id}` || cardBusy} onClick={(e) => { e.stopPropagation(); updateStatus(order.id, item.id, 'ready'); }} className="h-9 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 rounded-sm font-sans text-sm px-3" data-testid={`mark-ready-${item.id}`}>
                            Ready
                          </Button>
                        </div>
                        {item.instructions && (
                          <p className="mt-1 text-amber-300 text-sm font-sans bg-amber-500/10 border border-amber-500/30 rounded-sm px-2 py-1.5">
                            📝 {item.instructions}
                          </p>
                        )}
                      </div>
                    ))}
                    </div>
                    {/* Big tap target — entire bottom strip moves all items to Ready */}
                    <button
                      onClick={() => advanceOrder(order, 'preparing', 'ready')}
                      disabled={cardBusy}
                      className="w-full bg-emerald-500/15 hover:bg-emerald-500/25 active:bg-emerald-500/30 text-emerald-300 font-sans text-base font-semibold py-4 flex items-center justify-center gap-2 border-t border-emerald-500/20 transition-colors disabled:opacity-50"
                      data-testid={`ready-all-${order.id}`}
                    >
                      <ArrowRight className="w-4 h-4" /> Mark {prepItems.length > 1 ? `all ${prepItems.length} items` : 'order'} ready
                    </button>
                  </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Recently Served — collapsible drawer so chef can pull a served
              item back into prep when a customer returns/refuses it. Hidden
              by default to keep the panel focused on active work. */}
          {recentlyServed.length > 0 && (
            <div className="mt-6 border-t border-white/10 pt-4">
              <button
                type="button"
                onClick={() => setShowRecentlyServed(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-sm bg-white/[0.02] border border-white/10 hover:border-white/30 transition-colors"
                data-testid="toggle-recently-served"
              >
                <span className="flex items-center gap-2 text-sm font-sans text-zinc-300">
                  <Undo2 className="w-4 h-4 text-amber-300" />
                  Recently Served · last 60 min ({recentlyServed.reduce((n, o) => n + (o.items?.length || 0), 0)})
                </span>
                <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-sans">
                  {showRecentlyServed ? 'Hide' : 'Send back?'}
                </span>
              </button>
              {showRecentlyServed && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="recently-served-grid">
                  {recentlyServed.map(order => (
                    <div key={`served-${order.id}`} className="bg-white/[0.03] border border-zinc-700/40 rounded-sm overflow-hidden">
                      <div className="px-3 py-2 flex items-center justify-between border-b border-white/5">
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          {order.is_takeaway ? (
                            <span className="text-[10px] uppercase tracking-[0.18em] bg-orange-500/15 text-orange-400 border border-orange-500/40 px-2 py-0.5 rounded-sm font-sans font-bold">Takeaway</span>
                          ) : (
                            <span className="text-gold font-serif text-lg font-semibold">{tableLabel(order.table_number)}</span>
                          )}
                          <span className="text-xs text-zinc-500 font-sans">#{order.id.slice(0, 6)}</span>
                          {order.customer_name && <span className="text-xs text-zinc-400 font-sans truncate">· {order.customer_name}</span>}
                        </div>
                      </div>
                      {order.items.map(item => (
                        <div key={item.id} className="px-3 py-2 flex items-center justify-between border-t border-white/5">
                          <span className="text-zinc-300 font-sans text-sm truncate">{item.quantity}× {item.name}</span>
                          <Button
                            size="sm"
                            disabled={updating === `${order.id}-${item.id}`}
                            onClick={() => updateStatus(order.id, item.id, 'preparing')}
                            className="h-8 bg-amber-500/10 text-amber-300 border border-amber-500/30 hover:bg-amber-500/20 rounded-sm font-sans text-xs px-2.5"
                            data-testid={`send-back-${item.id}`}
                          >
                            <RotateCcw className="w-3 h-3 mr-1" /> Re-cook
                          </Button>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          </>
        )}
        </div>
      )}
    </div>
  );
}
